import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { getPlView, createLine, updateLine, deleteLine, reorderLines, renameSection, resetPlSection } from '../api/financials_pl';
import StructureEditor from '../components/StructureEditor';

/* Structure du Compte de résultat — édition des blocs et lignes de reporting.
   Déplacée depuis l'onglet « Structure » des États financiers vers Paramètres.
   Les lignes à rôle protégé (salaires, primes, charges sociales, impôts) sont
   non supprimables. La structure est indépendante de l'année ; getPlView exige
   une année (vue complète), on passe l'année active. */
const ROLE_SEMANTIC = {
  salary:         'Salaires',
  bonus:          'Primes & Bonus',
  social_charges: 'Charges sociales',
  income_tax:     'Impôts',
};

export default function PlStructurePage({ activeTenant, addToast, year }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id || null;
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);

  function reload() {
    if (!tenantId) { setView(null); return; }
    setLoading(true);
    getPlView(tenantId, year)
      .then(r => setView(r.data || null))
      .catch(() => addToast?.(p('Erreur chargement structure P&L'), 'error'))
      .finally(() => setLoading(false));
  }
  useEffect(reload, [tenantId, year]);   // eslint-disable-line

  async function act(promise, errLabel) {
    const res = await promise;
    if (res.data) reload(); else addToast?.(res.error || errLabel, 'error');
  }

  if (!tenantId) return <p className="page-table-empty">{p('Sélectionne une entreprise.')}</p>;
  if (loading && !view) return <p className="page-table-loading">{p('Chargement…')}</p>;
  if (!view) return null;

  return (
    <StructureEditor
      title={t('phrases:p_707b22b597')}
      maxCols={2}
      sections={view.sections || []}
      lines={view.lines || []}
      /* Charges directes sur deux niveaux, comme au compte de résultat :
         les lignes valorisées en stock (ou au rôle Achats / Variation de
         stock) forment « Coûts des produits vendus », le reste « Autres
         coûts directs » (2026-08-16). */
      groupsOf={(sec, secLines) => {
        if (sec.code !== 'cogs') return null;
        const isGoods = (l) => !!l.stock_linked
          || l.line_role === 'purchases' || l.line_role === 'stock_variation';
        const goods = secLines.filter(isGoods);
        const others = secLines.filter((l) => !isGoods(l));
        if (!goods.length || !others.length) return null;
        return [
          { code: 'cogs_goods', label: p('Coûts des produits vendus'), lines: goods },
          { code: 'cogs_other', label: p('Autres coûts directs'), lines: others },
        ];
      }}
      onCreate={(section, label) => act(createLine(tenantId, section, label), p('Erreur création ligne'))}
      onRename={(id, label) => act(updateLine(tenantId, id, { label }), p('Erreur renommage'))}
      onDelete={(id) => act(deleteLine(tenantId, id), p('Erreur suppression'))}
      onReorder={(ids) => act(reorderLines(tenantId, ids), p('Erreur réordonnancement'))}
      onRenameSection={(code, label) => act(renameSection(tenantId, code, label), p('Erreur renommage bloc'))}
      renderTag={(l) => l.line_role && ROLE_SEMANTIC[l.line_role] && <span className="fstruct-roletag">{p(ROLE_SEMANTIC[l.line_role])}</span>}
      renderActions={(l) => (
        <>
          {l.section === 'revenue' && (
            <select
              className="fstruct-revtype"
              title={p('Type de revenu (prestations / marchandises / autres)')}
              value={l.revenue_type || 'autres'}
              onChange={(e) => act(updateLine(tenantId, l.id, { revenue_type: e.target.value }), p('Erreur type de revenu'))}
            >
              <option value="prestations">{p('Prestations')}</option>
              <option value="marchandises">{p('Marchandises')}</option>
              <option value="autres">{p('Autres')}</option>
            </select>
          )}
          {/* Soumis à la TVA — par DÉFAUT partout où la TVA compte (revenus,
              charges directes, charges d'exploitation) ; clic pour exclure une
              ligne (export, exonéré, salaires refacturés…). La variation de
              stock n'est jamais une base TVA (exclue par rôle, pas de bouton). */}
          {['revenue', 'cogs', 'other_opex'].includes(l.section) && l.line_role !== 'stock_variation' && (
            <button
              type="button"
              className={`fstruct-vat${l.no_vat ? '' : ' is-on'}`}
              title={l.section === 'revenue'
                ? p('Soumis à la TVA (clic pour exclure — export / exonéré)')
                : p('TVA déductible sur cette charge (clic pour exclure — ligne sans impôt préalable)')}
              onClick={() => act(updateLine(tenantId, l.id, { no_vat: !l.no_vat }), p('Erreur TVA'))}
            >
              {p('TVA')}
            </button>
          )}
          {l.section === 'cogs' && (
            <select
              className="fstruct-revtype"
              title={p("Rôle d'ancrage : branche la ligne sur les calculs (achats & stock, coût des ventes). Le ↔ Stock ne règle que le lien stock.")}
              value={l.line_role || ''}
              onChange={(e) => {
                const role = e.target.value || null;
                const taken = role && (view.lines || []).some((x) => x.id !== l.id && x.line_role === role);
                if (taken) { addToast?.(p('Ce rôle est déjà porté par une autre ligne.'), 'error'); return; }
                act(updateLine(tenantId, l.id, { line_role: role }), p('Erreur rôle'));
              }}
            >
              <option value="">{p('— rôle')}</option>
              <option value="purchases">{p('Achats')}</option>
              <option value="subcontracting">{p('Sous-traitance')}</option>
              <option value="stock_variation">{p('Variation de stock')}</option>
              <option value="tooling">{p('Outillage')}</option>
              <option value="third_party">{p('Prestations tiers')}</option>
              <option value="mandate_fees">{p('Frais sur mandat')}</option>
            </select>
          )}
          {/* GROUPE du coût des ventes (2026-08-16) : c'est ce que l'ancien
              interrupteur « ↔ Stock » décidait en réalité — on le dit
              directement, sinon on ne trouve pas comment déplacer une ligne
              dans « Coûts des produits vendus ». Il porte toujours le lien au
              stock (variation de stock). */}
          {l.section === 'cogs' && (() => {
            const locked = l.line_role === 'purchases' || l.line_role === 'stock_variation';
            const goods = l.stock_linked || locked;
            return (
              <select
                className="fstruct-revtype"
                disabled={locked}
                title={locked
                  ? p('Coûts des produits vendus — obligatoire pour ce rôle')
                  : p('Groupe du coût des ventes. « Coûts des produits vendus » vaut aussi lien au stock (alimente la variation de stock).')}
                value={goods ? 'goods' : 'other'}
                onChange={(e) => act(updateLine(tenantId, l.id, { stock_linked: e.target.value === 'goods' }), p('Erreur groupe'))}
              >
                <option value="goods">{p('Produits vendus')}</option>
                <option value="other">{p('Autres directs')}</option>
              </select>
            );
          })()}
          <button
            type="button"
            className={`fstruct-eye${l.hidden ? ' is-off' : ''}`}
            title={t('phrases:p_d08452ab26')}
            aria-label={l.hidden ? p('Ligne masquée') : p('Ligne visible')}
            onClick={() => act(updateLine(tenantId, l.id, { hidden: !l.hidden }), p('Erreur masquage'))}
          >
            {l.hidden ? (
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                <path fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
                  d="M3 3l18 18M10.6 5.1A9.7 9.7 0 0112 5c5 0 9 4.5 10 7-.5 1.2-1.6 2.9-3.2 4.3M6.2 6.7C4 8.1 2.6 10.1 2 12c1 2.5 5 7 10 7 1.6 0 3-.4 4.3-1.1" />
                <path fill="none" stroke="currentColor" strokeWidth="1.7"
                  d="M9.5 9.6a3.5 3.5 0 004.9 4.9" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                <path fill="none" stroke="currentColor" strokeWidth="1.7"
                  d="M2 12c1-2.5 5-7 10-7s9 4.5 10 7c-1 2.5-5 7-10 7S3 14.5 2 12z" />
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.7" />
              </svg>
            )}
          </button>
        </>
      )}
      isProtected={(l) => !!l.line_role}
      renderSectionAction={(sec) => (
        <button
          type="button"
          className="fstruct-loadstd"
          title={t('phrases:p_4ddd0f4125')}
          onClick={() => {
            if (window.confirm(p('Charger la structure standard de « {{label}} » ? Les lignes du bloc sont remplacées ; les comptes sont re-mappés par libellé quand ils correspondent.').replace('{{label}}', sec.label))) {
              act(resetPlSection(tenantId, sec.code), p('Erreur chargement standard'));
            }
          }}
        >{p('Charger le standard')}</button>
      )}
    />
  );
}
